import { Component, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { countryDefaultValue } from "@models/address/address";
import { Company } from '@models/companies/company';
import { SecurityChange } from '@models/documents/security-change';
import { ClientSecurityTransaction } from '@models/security-transactions/client-security-transaction';
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from '@models/shares/security-registry-record';
import { SecurityType } from '@models/shares/security-type';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import {
  InputCertificateNumberComponent
} from '@shared/controls/input-certificate-number/input-certificate-number.component';
import { RadioComponent } from '@shared/controls/radio/radio.component';
import { CountriesService } from "@shared/services/countries.service";
import { Guid } from '@shared/utils/guid.helper';
import { setControlDisabled } from '@shared/utils/set-control-disabled';
import { DatepickerHelper } from '@shared/validators/date-picker-form-validators';
import { NumbersValidators } from '@shared/validators/numbers.validators';
import { debounceTime, finalize, Observable } from 'rxjs';
import {
  CompanySecurityBalance,
  CompanySecurityCancellation,
  CompanySecurityConsolidationSubdivision,
  CompanySecurityIssue,
  CompanySecurityTransfer,
} from "../../../models/shares";
import { ShareholderTypesEnum } from "../../../models/shares/company-security-issue.model";
import {
  HistoricalTransaction,
  HistoricalTransactionStepsEnum,
  historicalTransactionTypeOptions,
  HistoricalTransactionTypes
} from '../../../models/shares/historical-transaction.model';
import { findCorrespondingRecord } from "../../../utils/find-corresponding-record";
import {
  CompanySecurityBalanceBlock,
  CompanySecurityCancellationBlock,
  CompanySecurityConsolidationSubdivisionBlockComponent,
  CompanySecurityIssueBlock,
  CompanySecurityTransferFromBlock,
  CompanySecurityTransferToBlock
} from "../../shares";
import { StepperFormComponent } from "../../stepper-form";
import { BaseShareFormComponent } from '../base-share-form/base-share-form.component';
import { reloadShareholdersDebounceTimeMs } from "../constants/reload-shareholders-delay";

@Component({
  selector: 'app-share-issue',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    StepperFormComponent,
    RadioComponent,
    DatePickerComponent,
    InputCertificateNumberComponent,
    CompanySecurityBalanceBlock,
    CompanySecurityCancellationBlock,
    CompanySecurityConsolidationSubdivisionBlockComponent,
    CompanySecurityIssueBlock,
    CompanySecurityTransferFromBlock,
    CompanySecurityTransferToBlock,
  ],
  templateUrl: 'historical-transaction.component.html',
  styleUrls: [
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
    '../company-security-cancellation/company-security-cancellation.component.scss'
  ]
})
export class HistoricalTransactionComponent extends BaseShareFormComponent<HistoricalTransactionStepsEnum, HistoricalTransaction> implements OnInit {
  @ViewChild(CompanySecurityBalanceBlock) shareBalanceBlock!: CompanySecurityBalanceBlock;
  @ViewChild(CompanySecurityCancellationBlock) shareCancellationBlock!: CompanySecurityCancellationBlock;
  @ViewChild(CompanySecurityConsolidationSubdivisionBlockComponent) shareSubdivisionConversionBlockComponent!: CompanySecurityConsolidationSubdivisionBlockComponent;
  @ViewChild(CompanySecurityIssueBlock) shareIssueBlock!: CompanySecurityIssueBlock;
  @ViewChild(CompanySecurityTransferFromBlock) shareTransferFromBlock!: CompanySecurityTransferFromBlock;
  @ViewChild(CompanySecurityTransferToBlock) shareTransferToBlock!: CompanySecurityTransferToBlock;

  override readonly StepsEnum = HistoricalTransactionStepsEnum;

  readonly transactionTypeOptions = historicalTransactionTypeOptions;
  private readonly historicalTransactionSteps = [
    { step: HistoricalTransactionStepsEnum.TransactionType, label: 'Transaction Type' },
    { step: HistoricalTransactionStepsEnum.Balance, label: 'Balance' },
    { step: HistoricalTransactionStepsEnum.Issue, label: 'Issue' },
    { step: HistoricalTransactionStepsEnum.Cancellation, label: 'Cancellation' },
    { step: HistoricalTransactionStepsEnum.TransferFrom, label: 'Transfer From' },
    { step: HistoricalTransactionStepsEnum.TransferTo, label: 'Transfer To' },
    { step: HistoricalTransactionStepsEnum.Subdivision, label: 'Share Subdivision or Consolidation' },
  ];

  override steps = this.historicalTransactionSteps;
  override currentStepIndex = 0;
  override currentStep = this.steps[0].step;

  transactionsList: ClientSecurityTransaction[] = [];
  securityTypes: SecurityType[] = [];

  override stepperForm = new FormGroup({
    [HistoricalTransactionStepsEnum.TransactionType]: new FormGroup({
      transactionType: new FormControl<HistoricalTransactionTypes | null>(null, Validators.required),
    }),
    [HistoricalTransactionStepsEnum.Balance]: new FormGroup({
      balanceDate: new FormControl<Date | null>(null, [Validators.required]),
      ...CompanySecurityBalanceBlock.defineFormControls(),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger])
    }),
    [HistoricalTransactionStepsEnum.Issue]: new FormGroup({
      issueDate: new FormControl<Date | null>(null, [Validators.required]),
      ...CompanySecurityIssueBlock.defineFormControls(),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger])
    }),
    [HistoricalTransactionStepsEnum.Cancellation]: new FormGroup({
      cancellationDate: new FormControl<Date | null>(null, [Validators.required]),
      ...CompanySecurityCancellationBlock.defineFormControls(),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger])
    }),
    [HistoricalTransactionStepsEnum.TransferFrom]: new FormGroup({
      transferDate: new FormControl<Date | null>(null, [Validators.required]),
      ...CompanySecurityTransferFromBlock.defineFormControls()
    }),
    [HistoricalTransactionStepsEnum.TransferTo]: new FormGroup({
      ...CompanySecurityTransferToBlock.defineFormControls(),
      shareCertificateNumber: new FormControl<string | null>(null, [NumbersValidators.isInteger])
    }),
    [HistoricalTransactionStepsEnum.Subdivision]: CompanySecurityConsolidationSubdivisionBlockComponent.defineForm(),
  });

  constructor() {
    super();
    this.updateFormSteps();
    this.redirectAfterSubmit = false;

    this.transactionTypeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateFormSteps();
        this.disableSteps();
      });
  }

  disableSteps() {
    const transactionType = this.transactionTypeControl.value;

    setControlDisabled(this.stepperForm.controls[HistoricalTransactionStepsEnum.Balance], transactionType != 'balance');
    setControlDisabled(this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue], transactionType != 'issue');
    setControlDisabled(this.stepperForm.controls[HistoricalTransactionStepsEnum.Cancellation], transactionType != 'cancellation');
    setControlDisabled(this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferFrom], transactionType != 'transfer');
    setControlDisabled(this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferTo], transactionType != 'transfer');
    setControlDisabled(this.stepperForm.controls[HistoricalTransactionStepsEnum.Subdivision], transactionType != 'subdivision');

    // Special disable handlers
    switch (transactionType) {
      case 'balance': {
        this.shareBalanceBlock.form.controls.shareholderType.setValue(this.shareBalanceBlock.form.controls.shareholderType.value);
        break;
      }
      case "transfer": {
        this.shareTransferToBlock?.disableReadonlyControls();
        break;
      }
      case 'issue': {
        const shareIssueForm = this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue];
        shareIssueForm.controls.shareholderType.setValue(shareIssueForm.controls.shareholderType.value);
        break;
      }
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.maxDate = DatepickerHelper.getTodayStruct();

    this.listenChangeDateChanges();
  }

  override setupChange(change: SecurityChange<Company> = this.formModel) {
    if (!this.isEdit && change === this.formModel) return;

    this.setupFormChange(change);
  }

  setupFormChange(change: SecurityChange<Company>): void {
    const changeDate = change.changeDate;

    switch (change.$type) {
      case CompanySecurityIssue.$type : {
        const issueChange = change as CompanySecurityIssue;

        this.transactionTypeControl.setValue('issue');
        this.shareIssueBlock.setupChange(issueChange);
        this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue].controls.issueDate.setValue(changeDate);

        break;
      }
      case CompanySecurityCancellation.$type : {
        const cancellationChange = change as CompanySecurityCancellation;

        this.transactionTypeControl.setValue('cancellation');
        this.shareCancellationBlock.setupChange(cancellationChange);
        this.stepperForm.controls[HistoricalTransactionStepsEnum.Cancellation].controls.cancellationDate.setValue(changeDate);

        break;
      }
      case CompanySecurityTransfer.$type : {
        const transferChange = change as CompanySecurityTransfer;

        this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferFrom].controls.transferDate.setValue(changeDate);
        this.shareTransferToBlock?.disableReadonlyControls();
        this.transactionTypeControl.setValue('transfer');
        this.shareTransferFromBlock.setupChange(transferChange);
        this.shareTransferToBlock.setupChange(transferChange);

        break;
      }
      case CompanySecurityConsolidationSubdivision.$type : {
        const subdivisionChange = change as CompanySecurityConsolidationSubdivision;

        this.transactionTypeControl.setValue('subdivision');
        this.shareSubdivisionConversionBlockComponent.setupChange(subdivisionChange);
        this.stepperForm.controls[HistoricalTransactionStepsEnum.Subdivision].controls.subdivisionDate.setValue(changeDate);

        break;
      }
      case CompanySecurityBalance.$type : {
        const balanceChange = change as CompanySecurityBalance;

        this.transactionTypeControl.setValue('balance');
        this.shareBalanceBlock.setupChange(balanceChange);
        this.stepperForm.controls[HistoricalTransactionStepsEnum.Balance].controls.balanceDate.setValue(changeDate);

        break;
      }
    }
  }

  private listenChangeDateChanges() {
    this.stepperForm.controls[HistoricalTransactionStepsEnum.Balance].controls.balanceDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate);
      });

    this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue].controls.issueDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate);
      });

    this.stepperForm.controls[HistoricalTransactionStepsEnum.Cancellation].controls.cancellationDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate);
      });

    this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferFrom].controls.transferDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((transactionDate) => {
        if (!transactionDate) {
          return;
        }

        this.loadShareholders(transactionDate);
      });


    this.stepperForm.controls[HistoricalTransactionStepsEnum.Subdivision].controls.subdivisionDate.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(reloadShareholdersDebounceTimeMs),
      )
      .subscribe((dateOfResolution) => {
        if (!dateOfResolution) {
          return;
        }

        this.loadShareholders(dateOfResolution);
      });
  }

  private updateFormSteps(): void {
    const transactionType = this.transactionTypeControl.value;

    const steps = this.historicalTransactionSteps.filter((step) => {
      if (step.step === HistoricalTransactionStepsEnum.Balance) {
        return transactionType === 'balance';
      } else if (step.step === HistoricalTransactionStepsEnum.Issue) {
        return transactionType === 'issue';
      } else if (step.step === HistoricalTransactionStepsEnum.Cancellation) {
        return transactionType === 'cancellation';
      } else if (step.step === HistoricalTransactionStepsEnum.TransferFrom) {
        return transactionType === 'transfer';
      } else if (step.step === HistoricalTransactionStepsEnum.TransferTo) {
        return transactionType === 'transfer';
      } else if (step.step === HistoricalTransactionStepsEnum.Subdivision) {
        return transactionType === 'subdivision';
      }

      return true;
    });

    this.steps = steps;
  }

  override setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.steps[newStepIndex].step;
    this.currentStepIndex = newStepIndex;
  }

  override onShareholdersLoadSuccess(records: SecurityRegistryRecord[]): void {
    this.securityRegistryRecords = records;
    this.isLoading = false;

    const allSecurityTypes = records.flatMap(shareholder => shareholder.holdings.map(holding => holding.securityType));
    this.securityTypes = [...new Map<string, SecurityType>(allSecurityTypes.map(type => [type.securityTypeId, type])).values()]
      .filter(type => type.entityId && type.entityId !== Guid.EmptyGuid);
    this.cdr.detectChanges();
    this.setupChange();
    this.prefillForm();
  }

  override buildShareFormChange(): SecurityChange<Company> {
    const transactionType = this.transactionTypeControl.value;

    if (transactionType === 'balance') {
      return this.buildBalanceChange();
    } else if (transactionType === 'issue') {
      return this.buildIssueChange();
    } else if (transactionType === 'cancellation') {
      return this.buildCancellationChange();
    } else if (transactionType === 'transfer') {
      return this.buildTransferChange();
    } else if (transactionType === 'subdivision') {
      return this.buildSubdivisionChange();
    } else {
      throw new Error('Not implemented');
    }
  }

  buildBalanceChange(): SecurityChange<Company> {
    const companySecurityBalance = this.shareBalanceBlock.buildShareFormChange();

    companySecurityBalance.changeDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.Balance].value.balanceDate!;
    companySecurityBalance.balance.transactionDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.Balance].value.balanceDate!;
    companySecurityBalance.balance.shareCertificateNumber = this.stepperForm.controls[HistoricalTransactionStepsEnum.Balance].value.shareCertificateNumber ?? null;

    if (this.isEdit) {
      companySecurityBalance.balance.securityTransactionId = (this.formModel as CompanySecurityBalance).balance.securityTransactionId;
    }

    return companySecurityBalance.prepareToRequest();
  }

  buildIssueChange(): CompanySecurityIssue {
    const companySecurityIssue = this.shareIssueBlock.buildShareFormChange();

    companySecurityIssue.changeDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue].value.issueDate!;
    companySecurityIssue.issue.shareCertificateNumber = this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue].value.shareCertificateNumber ?? null;
    companySecurityIssue.issue.transactionDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.Issue].value.issueDate!;

    const companySecurityIssuePrepared = companySecurityIssue.prepareToRequest();

    if (this.isEdit) {
      companySecurityIssuePrepared.issue.securityTransactionId = (this.formModel as CompanySecurityIssue).issue.securityTransactionId;
      companySecurityIssuePrepared.issue.changeId = (this.formModel as CompanySecurityIssue).issue.changeId;
    }

    return companySecurityIssuePrepared;
  }

  buildCancellationChange(): CompanySecurityCancellation {
    const companySecurityCancellation = this.shareCancellationBlock.buildShareFormChange();

    companySecurityCancellation.changeDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.Cancellation].value.cancellationDate!;
    companySecurityCancellation.cancellation.shareCertificateNumber = this.stepperForm.controls[HistoricalTransactionStepsEnum.Cancellation].value.shareCertificateNumber ?? null;
    companySecurityCancellation.cancellation.transactionDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.Cancellation].value.cancellationDate!;

    const companySecurityCancellationPrepared = companySecurityCancellation.prepareToRequest();

    if (this.isEdit) {
      companySecurityCancellationPrepared.cancellation.securityTransactionId = (this.formModel as CompanySecurityCancellation).cancellation.securityTransactionId;
    }

    return companySecurityCancellationPrepared;
  }

  buildTransferChange(): CompanySecurityTransfer {
    const companySecurityTransfer = this.shareTransferFromBlock.buildChange();

    companySecurityTransfer.changeDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferFrom].value.transferDate!;
    companySecurityTransfer.transferFrom.transactionDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferFrom].value.transferDate!;
    companySecurityTransfer.transferTo.shareCertificateNumber = this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferTo].value.shareCertificateNumber ?? null;
    companySecurityTransfer.transferTo.transactionDate = this.stepperForm.controls[HistoricalTransactionStepsEnum.TransferFrom].value.transferDate!;

    const companySecurityTransferPrepared = companySecurityTransfer.prepareToRequest();

    if (this.isEdit) {
      const change = this.formModel as CompanySecurityTransfer
      companySecurityTransferPrepared.transferFrom.changeId = change.transferFrom.changeId;
      companySecurityTransferPrepared.transferFrom.entityId = change.transferFrom.entityId;
      companySecurityTransferPrepared.transferFrom.securityTransactionId = change.transferFrom.securityTransactionId;

      companySecurityTransferPrepared.transferTo.changeId = change.transferTo.changeId;
      companySecurityTransferPrepared.transferTo.entityId = change.transferTo.entityId;
      companySecurityTransferPrepared.transferTo.securityTransactionId = change.transferTo.securityTransactionId;
    }

    return companySecurityTransferPrepared;
  }

  buildSubdivisionChange(): CompanySecurityConsolidationSubdivision {
    const companySecurityConsolidationSubdivision = this.shareSubdivisionConversionBlockComponent.buildShareFormChange();
    const form = this.stepperForm.controls[HistoricalTransactionStepsEnum.Subdivision];
    const subdivisionDate = form.controls.subdivisionDate.value!;

    companySecurityConsolidationSubdivision.dateOfResolution = subdivisionDate;
    companySecurityConsolidationSubdivision.changeDate = subdivisionDate;
    companySecurityConsolidationSubdivision.convertShares = true;

    return companySecurityConsolidationSubdivision.prepareToRequest();
  }

  public override submit(): void {
    if (this.stepperForm.invalid) {
      this.toastr.error('Please fill required fields', 'Error');
      this.stepperForm.controls[this.currentStep]?.markAllAsTouched();

      return;
    }

    const change = this.buildShareFormChange();

    this.isLoading = true;
    this.stepperForm.disable();

    const request = this.isEdit
      ? this.updateHistoricalTransaction(change)
      : this.addHistoricalTransaction(change);

    request
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.stepperForm.enable();
          this.afterSubmit([change]);
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success('Transaction added.', 'Success');
          this.activeModal.close([change]);
        },
        error: (error) => {
          console.warn(error);
          this.toastr.error('Failed to add Transaction.', 'Error');
          this.transactionTypeControl.setValue(this.transactionTypeControl.value);
        }
      });
  }

  addHistoricalTransaction(change: SecurityChange<Company>): Observable<void> {
    return this.securityService.addHistoricalTransaction(this.companyChangeData.entityId, change);
  }

  updateHistoricalTransaction(change: SecurityChange<Company>): Observable<void> {
    change.changeId = this.formModel.changeId;
    return this.securityService.updateTransaction(this.companyChangeData.entityId, change);
  }

  override prefillForm(shareGroupView = this.prefillData, shouldPrefillSecurityType = this.shouldPrefillSecurityType): void {
    if (!shareGroupView?.shareholders?.length) {
      // group by class case
      this.prefillData = null;
      return;
    }

    const holders = shareGroupView.shareholders;
    const callRecord = shareGroupView.callRecords[0];
    const record = callRecord?.record ?? null;
    const transactionDate = DatepickerHelper.getToday();

    const correspondingRecord = record
      ? findCorrespondingRecord(record, this.securityRegistryRecords)
      : null;
    const securityRegistryRecordGroup = correspondingRecord
      ? [correspondingRecord]
      : null;
    const recordSecurityHoldingId = correspondingRecord?.holdings?.[0]?.securityHoldingId ?? null;

    this.stepperForm.patchValue({
      [this.StepsEnum.Balance]: {
        balanceDate: transactionDate,
        shareholderType: ShareholderTypesEnum.ExistingShareholder,
        securityRegistryRecordGroup,
      },
      [this.StepsEnum.Cancellation]: {
        cancellationDate: transactionDate,
        securityRegistryRecordGroup
      },
      [this.StepsEnum.TransferFrom]: {
        transferDate: transactionDate,
        shareholder: {
          securityRegistryRecordGroup
        }
      },
      [this.StepsEnum.Issue]: {
        issueDate: transactionDate,
        shareholderType: ShareholderTypesEnum.ExistingShareholder,
        securityRegistryRecordGroup,
      },
      [this.StepsEnum.Subdivision]: {
        subdivisionDate: transactionDate
      }
    });

    const selectedSecurityRegistryGroup = this.shareCancellationBlock.groupedSecurityRegistryRecords.find((group) => group.some((record) => record?.holdings.some((holder) => holder.securityHoldingId === recordSecurityHoldingId)));

    if (selectedSecurityRegistryGroup) {
      const selectedSecurityHolding = selectedSecurityRegistryGroup
        .flatMap((record) => record.holdings)?.find(holding => holding.securityHoldingId === recordSecurityHoldingId);

      if (selectedSecurityHolding !== undefined) {
        this.shareCancellationBlock.onSelectCurrentShareholder(selectedSecurityRegistryGroup, false);
        this.shareTransferFromBlock.onSelectCurrentShareholder(selectedSecurityRegistryGroup, false);

        if (shouldPrefillSecurityType) {
          const securityTypeId = selectedSecurityHolding?.securityType.securityTypeId ?? null;

          this.stepperForm.controls[this.StepsEnum.Balance].controls.securityTypeId.patchValue(securityTypeId);
          this.stepperForm.controls[this.StepsEnum.Issue].controls.securityTypeId.patchValue(securityTypeId);

          const securityHoldingId = selectedSecurityHolding?.securityHoldingId ?? null;

          this.stepperForm.controls[this.StepsEnum.Cancellation].controls.securityHoldingId.patchValue(securityHoldingId);
          this.stepperForm.controls[this.StepsEnum.TransferFrom].controls.securityHoldingId.patchValue(securityHoldingId);
        }
      }
    }

    if (!correspondingRecord && holders.length) {
      const balanceForm = this.stepperForm.controls[this.StepsEnum.Balance];
      const shareIssueForm = this.stepperForm.controls[this.StepsEnum.Issue];

      if (holders.length > 1) {
        const shareholderType = ShareholderTypesEnum.JointShareholder;
        const joint = holders;

        balanceForm.patchValue({ shareholderType, joint });
        shareIssueForm.patchValue({ shareholderType, joint });
      } else if (holders[0].$type == IndividualHolderModel.$type && holders[0]['individualData']) {
        const individualHolderModel = holders[0] as IndividualHolderModel;
        const shareholderType = ShareholderTypesEnum.Individual;
        const newIndividualShareholder = {
          ...individualHolderModel.individualData,
          address: {
            ...individualHolderModel.address,
            country: individualHolderModel.address.country || countryDefaultValue
          }
        };

        balanceForm.patchValue({ shareholderType, newIndividualShareholder });
        shareIssueForm.patchValue({ shareholderType, newIndividualShareholder });
      } else if (holders[0].$type == CorporateHolderModel.$type && holders[0]['entityData']) {
        const shareholderType = ShareholderTypesEnum.Corporate;
        const corporateHolderModel = holders[0] as CorporateHolderModel;
        const country = corporateHolderModel.entityData.registeredAddress.country || countryDefaultValue;

        const valueToPatch = {
          shareholderType,
          name: corporateHolderModel.entityData.name.trim(),
          acn: corporateHolderModel.entityData.entityNumber ?? null,
          isOverseasCompany: !CountriesService.isAustralianStateOrAustralia(country),
          registeredAddress: {
            ...corporateHolderModel.entityData.registeredAddress,
            country,
          }
        };

        balanceForm.patchValue(valueToPatch);
        shareIssueForm.patchValue(valueToPatch);
      }
    }

    super.prefillForm();
  }

  override handleShareIssue(): void {}

  get transactionTypeControl(): FormControl<HistoricalTransactionTypes | null> {
    return this.stepperForm.controls[HistoricalTransactionStepsEnum.TransactionType].controls.transactionType;
  }
}

